import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2DataGrid,
  V2EmptyState,
  V2FormField,
  V2FormGrid,
  LoadingState,
  StatePanel,
  V2CloseButton,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type { FiscalPeriodListResponse, FiscalYearListResponse } from '../../lib/types/finance.js';

interface FixedAssetItem {
  readonly assetId: string;
  readonly categoryId: string;
  readonly code: string;
  readonly name: string;
  readonly acquisitionDate: string;
  readonly capitalizationDate: string | null;
  readonly acquisitionCostRial: string;
  readonly salvageValueRial: string;
  readonly usefulLifeMonths: number;
  readonly status: 'draft' | 'capitalized' | 'in_service' | 'disposed' | 'scrapped';
  readonly disposalDate: string | null;
  readonly disposalAmountRial: string | null;
  readonly disposalGainLossRial: string | null;
  readonly disposalJournalId: string | null;
}

interface CategoryItem {
  readonly categoryId: string;
  readonly code: string;
  readonly name: string;
  readonly usefulLifeMonths: number;
}

export function FixedAssetsPage() {
  const { unit, label } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDepreciateModal, setShowDepreciateModal] = useState(false);

  // Create form state
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [acquisitionDate, setAcquisitionDate] = useState('');
  const [acquisitionCostRial, setAcquisitionCostRial] = useState('');
  const [salvageValueRial, setSalvageValueRial] = useState('');
  const [usefulLifeMonths, setUsefulLifeMonths] = useState(60);

  // Depreciation run state
  const [depFiscalYearId, setDepFiscalYearId] = useState('');
  const [depFiscalPeriodId, setDepFiscalPeriodId] = useState('');
  const [depRunDate, setDepRunDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const assets = useApiGet<{ items: FixedAssetItem[] }>(
    slug ? `${base}/api/v1/finance/fixed-assets` : null,
    [slug, refreshKey],
  );

  const categories = useApiGet<{ items: CategoryItem[] }>(
    slug ? `${base}/api/v1/finance/fixed-assets/categories` : null,
    [slug],
  );

  const years = useApiGet<FiscalYearListResponse>(
    slug ? `${base}/api/v1/finance/fiscal-years` : null,
    [slug],
  );

  const periods = useApiGet<FiscalPeriodListResponse>(
    slug ? `${base}/api/v1/accounting/fiscal-periods` : null,
    [slug],
  );

  if (!slug) return null;

  async function handleCreateAsset(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await apiPost(`${base}/api/v1/finance/fixed-assets`, {
        categoryId,
        code,
        name,
        acquisitionDate,
        acquisitionCostRial,
        ...(salvageValueRial ? { salvageValueRial } : {}),
        usefulLifeMonths,
      });
      setShowCreateModal(false);
      setRefreshKey((k) => k + 1);
      setCode('');
      setName('');
      setAcquisitionCostRial('');
      setSalvageValueRial('');
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'خطا در ثبت دارایی ثابت');
    } finally {
      setBusy(false);
    }
  }

  async function handleRunDepreciation(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await apiPost(`${base}/api/v1/finance/fixed-assets/depreciation-runs`, {
        fiscalYearId: depFiscalYearId,
        fiscalPeriodId: depFiscalPeriodId,
        runDate: depRunDate,
      });
      setShowDepreciateModal(false);
      setRefreshKey((k) => k + 1);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'خطا در محاسبهٔ استهلاک');
    } finally {
      setBusy(false);
    }
  }

  async function handleCapitalize(assetId: string) {
    setBusy(true);
    setActionError(null);
    try {
      await apiPost(`${base}/api/v1/finance/fixed-assets/${assetId}/capitalize`, {
        capitalizationDate: new Date().toISOString().slice(0, 10),
      });
      setRefreshKey((k) => k + 1);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'خطا در بهره‌برداری دارایی');
    } finally {
      setBusy(false);
    }
  }

  const statusTones: Record<string, 'neutral' | 'success' | 'warn' | 'danger'> = {
    draft: 'neutral',
    capitalized: 'success',
    in_service: 'success',
    disposed: 'warn',
    scrapped: 'danger',
  };

  const statusLabels: Record<string, string> = {
    draft: 'پیش‌نویس',
    capitalized: 'بهره‌برداری‌شده',
    in_service: 'در حال بهره‌برداری',
    disposed: 'واگذارشده',
    scrapped: 'اسقاط‌شده',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader
          title="دفتر دارایی‌های ثابت"
          subtitle={`مدیریت اموال، استهلاک خط مستقیم و اسقاط | ارقام به ${label}`}
          actions={
            <div style={{ display: 'flex', gap: 8 }}>
              <V2Button variant="secondary" size="sm" onClick={() => setShowDepreciateModal(true)}>
                محاسبهٔ استهلاک دوره‌ای
              </V2Button>
              <V2Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                ثبت دارایی جدید
              </V2Button>
            </div>
          }
        />
      </V2Card>

      {actionError && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: 'var(--sk-danger-soft, #fee2e2)',
            borderRadius: '8px',
            color: 'var(--sk-danger, #ef4444)',
            fontSize: '13px',
          }}
        >
          {actionError}
        </div>
      )}

      {assets.status === 'loading' ? <LoadingState /> : null}
      {assets.status === 'error' ? (
        <StatePanel
          variant={assets.error.status === 403 ? 'permission_denied' : 'network_error'}
          title="خطا در دریافت فهرست دارایی‌ها"
        />
      ) : null}

      {assets.status === 'ready' &&
        (assets.data.items.length === 0 ? (
          <V2EmptyState
            icon="finance"
            title="هیچ دارایی ثابتی ثبت نشده است"
            description="برای شروع، دارایی ثابت جدیدی ثبت کرده یا دسته‌بندی‌های اموال را تعریف کنید."
          />
        ) : (
          <V2DataGrid
            title="فهرست دارایی‌های ثبت‌شده"
            subtitle={`${assets.data.items.length.toLocaleString('fa-IR')} مورد`}
            rowKey={(a: FixedAssetItem) => a.assetId}
            pageSize={20}
            columns={[
              {
                key: 'code',
                header: 'کد دارایی',
                width: 120,
                render: (a: FixedAssetItem) => <span className="ltr sk-num">{a.code}</span>,
              },
              {
                key: 'name',
                header: 'عنوان دارایی',
                width: 220,
                render: (a: FixedAssetItem) => <span style={{ fontWeight: 600 }}>{a.name}</span>,
              },
              {
                key: 'acquisitionDate',
                header: 'تاریخ تحصیل',
                width: 130,
                render: (a: FixedAssetItem) => (
                  <span className="sk-num">{displayJalaliDate(a.acquisitionDate)}</span>
                ),
              },
              {
                key: 'cost',
                header: 'بهای تمام‌شده',
                width: 160,
                render: (a: FixedAssetItem) => (
                  <span className="sk-num">{formatRialFa(a.acquisitionCostRial, unit)}</span>
                ),
              },
              {
                key: 'status',
                header: 'وضعیت',
                width: 140,
                render: (a: FixedAssetItem) => (
                  <V2Badge tone={statusTones[a.status] ?? 'neutral'}>
                    {statusLabels[a.status] ?? a.status}
                  </V2Badge>
                ),
              },
              {
                key: 'actions',
                header: 'عملیات',
                width: 140,
                render: (a: FixedAssetItem) =>
                  a.status === 'draft' ? (
                    <V2Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleCapitalize(a.assetId)}
                      disabled={busy}
                    >
                      بهره‌برداری
                    </V2Button>
                  ) : null,
              },
            ]}
            rows={assets.data.items}
          />
        ))}

      {/* Modal: Create Asset */}
      {showCreateModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--sk-bg-card, #fff)',
              borderRadius: '12px',
              padding: '24px',
              width: '500px',
              maxWidth: '90vw',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setShowCreateModal(false)} />
            </div>
            <h3 style={{ margin: '0 0 16px' }}>ثبت دارایی ثابت جدید</h3>
            <form onSubmit={handleCreateAsset}>
              <V2FormGrid>
                <V2FormField label="کد دارایی" required width="medium">
                  <input
                    className="sk-input ltr"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                  />
                </V2FormField>
                <V2FormField label="عنوان دارایی" required width="medium">
                  <input
                    className="sk-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </V2FormField>
                <V2FormField label="دستهٔ دارایی" required width="medium">
                  <select
                    className="sk-select"
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    required
                  >
                    <option value="">انتخاب دسته</option>
                    {categories.status === 'ready' &&
                      categories.data.items.map((c) => (
                        <option key={c.categoryId} value={c.categoryId}>
                          {c.code} — {c.name}
                        </option>
                      ))}
                  </select>
                </V2FormField>
                <V2FormField label="تاریخ تحصیل" required width="medium">
                  <JalaliDateInput value={acquisitionDate} onChange={setAcquisitionDate} />
                </V2FormField>
                <V2FormField label="بهای تمام‌شده (ریال)" required width="medium">
                  <input
                    className="sk-input ltr sk-num"
                    value={acquisitionCostRial}
                    onChange={(e) => setAcquisitionCostRial(e.target.value)}
                    required
                  />
                </V2FormField>
                <V2FormField label="ارزش اسقاط (ریال)" width="medium">
                  <input
                    className="sk-input ltr sk-num"
                    value={salvageValueRial}
                    onChange={(e) => setSalvageValueRial(e.target.value)}
                  />
                </V2FormField>
                <V2FormField label="عمر مفید (ماه)" width="medium">
                  <input
                    type="number"
                    className="sk-input ltr sk-num"
                    value={usefulLifeMonths}
                    onChange={(e) => setUsefulLifeMonths(Number(e.target.value))}
                  />
                </V2FormField>
              </V2FormGrid>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <V2Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  انصراف
                </V2Button>
                <V2Button type="submit" variant="primary" disabled={busy}>
                  ثبت دارایی
                </V2Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Depreciation Run */}
      {showDepreciateModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--sk-bg-card, #fff)',
              borderRadius: '12px',
              padding: '24px',
              width: '450px',
              maxWidth: '90vw',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setShowDepreciateModal(false)} />
            </div>
            <h3 style={{ margin: '0 0 16px' }}>محاسبه و ثبت استهلاک دوره‌ای</h3>
            <p style={{ fontSize: '13px', color: 'var(--sk-fg-muted)', marginBottom: 16 }}>
              این عملیات استهلاک همهٔ دارایی‌های در حال بهره‌برداری را بر اساس روش خط مستقیم محاسبه
              کرده و سند پیش‌نویس حسابداری ایجاد می‌کند.
            </p>
            <form onSubmit={handleRunDepreciation}>
              <V2FormGrid>
                <V2FormField label="سال مالی" required width="medium">
                  <select
                    className="sk-select"
                    value={depFiscalYearId}
                    onChange={(e) => setDepFiscalYearId(e.target.value)}
                    required
                  >
                    <option value="">انتخاب سال</option>
                    {years.status === 'ready' &&
                      years.data.items.map((y) => (
                        <option key={y.fiscalYearId} value={y.fiscalYearId}>
                          {y.name}
                        </option>
                      ))}
                  </select>
                </V2FormField>
                <V2FormField label="دوره مالی" required width="medium">
                  <select
                    className="sk-select"
                    value={depFiscalPeriodId}
                    onChange={(e) => setDepFiscalPeriodId(e.target.value)}
                    required
                  >
                    <option value="">انتخاب دوره</option>
                    {periods.status === 'ready' &&
                      periods.data.items
                        .filter((p) => !depFiscalYearId || p.fiscalYearId === depFiscalYearId)
                        .map((p) => (
                          <option key={p.fiscalPeriodId} value={p.fiscalPeriodId}>
                            {p.name} ({p.postingState === 'open' ? 'باز' : 'بسته'})
                          </option>
                        ))}
                  </select>
                </V2FormField>
                <V2FormField label="تاریخ ثبت سند" required width="medium">
                  <JalaliDateInput value={depRunDate} onChange={setDepRunDate} />
                </V2FormField>
              </V2FormGrid>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <V2Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowDepreciateModal(false)}
                >
                  انصراف
                </V2Button>
                <V2Button type="submit" variant="primary" disabled={busy}>
                  محاسبه و ایجاد سند
                </V2Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
